import axios from "axios";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

const REQUEST_START_TIME = Symbol("startTime");
const REQUEST_LOADING_STATE = Symbol("loadingState");
const SKIP_TOAST = Symbol("skipToast");
const SKIP_LOADER = Symbol("skipLoader");
const SKIP_ERROR_TOAST = Symbol("skipErrorToast");
let isRedirecting = false;

const httpClient = axios.create({
  // baseURL: "http://172.16.100.219:9765/api/v1",
  // baseURL: "http://132.148.76.219:9765/api/v1", //client server
  baseURL: "https://checkthevolume.com/api/v1", //client server
  // baseURL: "http://localhost:9765/api/v1",
  // baseURL: "https://webmobrildemo.com/volumetv/api/v1",
});

httpClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("volumeToken");
    const authToken = localStorage.getItem("volumeAuthToken");
    if (token && token.trim() !== "") {
      config.headers["Authorization"] = `Bearer ${token}`;
    } else if (authToken && authToken.trim() !== "") {
      config.headers["Authorization"] = `Bearer ${authToken}`;
    } else {
      delete config.headers["Authorization"];
    }

    config[REQUEST_START_TIME] = new Date();

    // Check if skipToast is set in config (from axios request options)
    if (config.skipToast) {
      config[SKIP_TOAST] = true;
      delete config.skipToast; // Remove from config to avoid axios issues
    }

    // Check if skipErrorToast is set (suppress error toasts)
    if (config.skipErrorToast) {
      config[SKIP_ERROR_TOAST] = true;
      delete config.skipErrorToast;
    }

    // Check if skipLoader is set (e.g. for content-play-count)
    if (config.skipLoader) {
      config[SKIP_LOADER] = true;
      delete config.skipLoader;
    }

    // Track loading state for POST, PATCH, PUT, DELETE requests (unless skipLoader)
    const method = config.method?.toUpperCase();
    if (
      ["POST", "PATCH", "PUT", "DELETE"].includes(method) &&
      !config[SKIP_LOADER]
    ) {
      config[REQUEST_LOADING_STATE] = true;
      // Dispatch loading event for components to show loaders
      window.dispatchEvent(
        new CustomEvent("apiLoading", {
          detail: { loading: true, method, url: config.url },
        }),
      );
    }

    return config;
  },
  (error) => {
    console.error("Request error:", error);
    // Stop loading on request error
    window.dispatchEvent(
      new CustomEvent("apiLoading", { detail: { loading: false } }),
    );
    return Promise.reject(error);
  },
);

httpClient.interceptors.response.use(
  (response) => {
    const startTime = response.config[REQUEST_START_TIME];
    if (startTime) {
      const responseTime = new Date() - startTime;
      console.log(`API ${response.config.url} took ${responseTime}ms`);
    }

    console.log("API Debug Check:", {
      url: response.config.url,
      method: response.config.method,
    });

    // Show success toast for POST, PATCH, PUT, DELETE requests (unless skipped)
    const method = response.config.method?.toUpperCase();
    const skipToast =
      response.config[SKIP_TOAST] ||
      response.config.url?.includes("/common/paypal/");

    if (["POST", "PATCH", "PUT", "DELETE"].includes(method)) {
      // Stop loading only if we showed loader (did not skip)
      if (!response.config[SKIP_LOADER]) {
        window.dispatchEvent(
          new CustomEvent("apiLoading", { detail: { loading: false } }),
        );
      }

      // Show success toast only if not skipped
      if (!skipToast) {
        const successMessage =
          response.data?.message ||
          (method === "POST"
            ? "Created successfully"
            : method === "PATCH"
              ? "Updated successfully"
              : method === "PUT"
                ? "Updated successfully"
                : method === "DELETE"
                  ? "Deleted successfully"
                  : "Operation successful");

        toast.success(successMessage, {
          position: "top-right",
          autoClose: 3000,
        });
      }
    }

    return response;
  },
  (error) => {
    const status = error?.response?.status;
    const errorMessage = error?.response?.data?.message || "An error occurred";
    const originalRequest = error.config;

    const skipToast = originalRequest?.[SKIP_ERROR_TOAST];
    // Stop loading on error (only if we showed loader)
    const method = originalRequest?.method?.toUpperCase();
    if (
      ["POST", "PATCH", "PUT", "DELETE"].includes(method) &&
      !originalRequest?.[SKIP_LOADER]
    ) {
      window.dispatchEvent(
        new CustomEvent("apiLoading", { detail: { loading: false } }),
      );
    }

    console.error("API Error:", {
      status,
      message: errorMessage,
      url: originalRequest?.url,
      method: originalRequest?.method,
    });

    if (skipToast) {
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry && !isRedirecting) {
      const token = localStorage.getItem("volumeToken");

      if (token && token.trim() !== "") {
        isRedirecting = true;
        originalRequest._retry = true;

        Swal.fire({
          title: "Session Expired",
          text:
            errorMessage || "Your session has expired. Please log in again.",
          icon: "warning",
          confirmButtonText: "OK",
          allowOutsideClick: false,
        }).then(() => {
          localStorage.removeItem("volumeToken");
          isRedirecting = false;
          window.location.href = "/login";
        });

        return Promise.reject(error);
      } else {
        toast.error("Please login to access this resource");
        return Promise.reject(error);
      }
    }

    switch (status) {
      case 403: {
        const currentUser = localStorage.getItem("current_user");
        const upgradePath =
          currentUser === "Artist" ? "/artist/subscription" : "/subscription";
        Swal.fire({
          title: "Access Denied",
          text: errorMessage || "You don't have access to this feature.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Upgrade",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#007bff",
        }).then((result) => {
          if (result.isConfirmed) {
            window.location.href = upgradePath;
          }
        });
        break;
      }
      case 404:
        toast.warn(errorMessage || "Resource not found");
        break;
      case 429:
        toast.warn("Too Many Requests: Please wait before trying again");
        break;
      case 500:
        toast.error("Server Error: " + errorMessage);
        break;
      default:
        if (error.code === "ECONNABORTED") {
          toast.error("Request timeout. Please try again.");
        } else if (error.message === "Network Error") {
          toast.error("Network error. Please check your connection.");
        } else if (!error.response) {
          toast.error("Unable to connect to server.");
        } else {
          toast.error(errorMessage);
        }
    }

    return Promise.reject(error);
  },
);

httpClient.CancelToken = axios.CancelToken;
httpClient.isCancel = axios.isCancel;

export default httpClient;
